import React, { useState, useEffect, useCallback } from 'react';
import { PlusIcon, CheckIcon, XMarkIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { bookingsAPI } from '../services/api';
import { Booking, BookingApprovalStatus, bookingStatusLabel, bookingStatusClass } from '../types/booking';
import { useAuth } from '../contexts/AuthContext';
import { isAdmin } from '../types/auth';
import { usePageTitle } from '../hooks/usePageTitle';
import { useToast } from '../contexts/ToastContext';
import { getErrorMessage } from '../utils/errorMessage';
import BookingForm from '../components/bookings/BookingForm';
import DeleteConfirmModal from '../components/ui/DeleteConfirmModal';
import { useEscapeToClose } from '../hooks/useEscapeToClose';

const formatRange = (start: string, end: string): string => {
  const s = new Date(start);
  const e = new Date(end);
  const time = (d: Date) => d.toLocaleTimeString('hu-HU', { hour: '2-digit', minute: '2-digit' });
  return `${s.toLocaleDateString('hu-HU')} ${time(s)} - ${time(e)}`;
};

/**
 * Pályafoglalások. Adminnak az elbírálásra váró foglalások listája, vendégnek a
 * sajátjai - ugyanaz az oldal, mert ugyanarról a néhány sorról szól.
 */
const Bookings: React.FC = () => {
  const { user } = useAuth();
  const { showToast } = useToast();
  usePageTitle('Foglalások');

  const admin = isAdmin(user);

  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [canCreate, setCanCreate] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Booking | null>(null);
  const [toDelete, setToDelete] = useState<Booking | null>(null);
  const [rejecting, setRejecting] = useState<Booking | null>(null);
  useEscapeToClose(!!rejecting, () => setRejecting(null));
  const [rejectReason, setRejectReason] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [list, allowed] = await Promise.all([
        admin ? bookingsAPI.getPending() : bookingsAPI.getMine(),
        bookingsAPI.canCreate(),
      ]);
      setBookings(list);
      setCanCreate(allowed);
    } catch (error) {
      console.error('A foglalások betöltése nem sikerült:', error);
    } finally {
      setLoading(false);
    }
  }, [admin]);

  useEffect(() => {
    load();
  }, [load]);

  const handleApprove = async (booking: Booking) => {
    try {
      await bookingsAPI.approve(booking.id);
      showToast('Foglalás jóváhagyva.', 'success');
      await load();
    } catch (err) {
      showToast(getErrorMessage(err, 'A jóváhagyás nem sikerült.'), 'error');
    }
  };

  const handleReject = async () => {
    if (!rejecting) return;

    try {
      await bookingsAPI.reject(rejecting.id, rejectReason.trim() || undefined);
      showToast('Foglalás elutasítva.', 'success');
      setRejecting(null);
      setRejectReason('');
      await load();
    } catch (err) {
      showToast(getErrorMessage(err, 'Az elutasítás nem sikerült.'), 'error');
    }
  };

  const handleDelete = async () => {
    if (!toDelete) return;

    try {
      await bookingsAPI.delete(toDelete.id);
      setToDelete(null);
      await load();
    } catch (err) {
      showToast(getErrorMessage(err, 'A törlés nem sikerült.'), 'error');
    }
  };

  return (
    <div>
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {admin ? 'Foglalások elbírálása' : 'Foglalásaim'}
          </h1>
          <p className="text-sm text-gray-500">
            {admin
              ? 'A vendégek által beküldött, még el nem bírált pályafoglalások.'
              : 'A beküldött foglalásaid és az állapotuk.'}
          </p>
        </div>

        {!admin && canCreate && (
          <button
            onClick={() => {
              setEditing(null);
              setFormOpen(true);
            }}
            className="inline-flex items-center justify-center rounded-md border border-transparent bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700"
          >
            <PlusIcon className="mr-2 h-5 w-5" />
            Új foglalás
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex h-48 items-center justify-center text-gray-500">Betöltés...</div>
      ) : bookings.length === 0 ? (
        <div className="rounded-lg bg-white py-12 text-center shadow">
          <p className="text-gray-500">
            {admin ? 'Nincs elbírálásra váró foglalás.' : 'Még nincs foglalásod.'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {bookings.map((booking) => (
            <div key={booking.id} className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
              <div className="mb-2 flex flex-wrap items-center gap-1.5">
                <span
                  className={`inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ${bookingStatusClass(
                    booking.approvalStatus
                  )}`}
                >
                  {bookingStatusLabel(booking.approvalStatus)}
                </span>
                {booking.guestTeam && (
                  <span className="inline-flex items-center whitespace-nowrap rounded-full bg-primary-100 px-2 py-0.5 text-xs font-medium text-primary-800">
                    {booking.guestTeam}
                  </span>
                )}
              </div>

              <div className="font-medium text-gray-900 break-words hyphens-auto">{booking.title}</div>
              <div className="mt-1 text-sm text-gray-600">{formatRange(booking.startTime, booking.endTime)}</div>
              <div className="text-sm text-gray-500">{booking.venueName ?? 'Pálya nincs megadva'}</div>

              {booking.approvalStatus === BookingApprovalStatus.Rejected && booking.rejectionReason && (
                <p className="mt-2 rounded-md bg-red-50 p-2 text-sm text-red-800">
                  Indoklás: {booking.rejectionReason}
                </p>
              )}

              {admin && (
                <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-gray-100 pt-3">
                  <span className="text-sm text-gray-500">Beküldte: {booking.createdByUserName}</span>
                  <div className="ml-auto flex items-center gap-2">
                    <button
                      onClick={() => handleApprove(booking)}
                      className="inline-flex items-center gap-1 rounded-md bg-green-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-700"
                    >
                      <CheckIcon className="h-4 w-4" />
                      Jóváhagyás
                    </button>
                    <button
                      onClick={() => {
                        setRejecting(booking);
                        setRejectReason('');
                      }}
                      className="inline-flex items-center gap-1 rounded-md border border-red-300 px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-50"
                    >
                      <XMarkIcon className="h-4 w-4" />
                      Elutasítás
                    </button>
                  </div>
                </div>
              )}

              {!admin && (
                <div className="mt-3 flex items-center justify-end gap-1 border-t border-gray-100 pt-2">
                  <button
                    onClick={() => {
                      setEditing(booking);
                      setFormOpen(true);
                    }}
                    aria-label="Módosítás"
                    className="flex h-9 w-9 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-primary-600"
                  >
                    <PencilIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => setToDelete(booking)}
                    aria-label="Lemondás"
                    className="flex h-9 w-9 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-red-600"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <BookingForm
        isOpen={formOpen}
        onClose={() => setFormOpen(false)}
        booking={editing}
        onSaved={load}
      />

      <DeleteConfirmModal
        isOpen={toDelete !== null}
        onClose={() => setToDelete(null)}
        onConfirm={handleDelete}
        title="Foglalás lemondása"
        message="Biztosan lemondod ezt a foglalást?"
        itemName={toDelete?.title ?? ''}
      />

      {rejecting && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label="Foglalás elutasítása">
          <div className="fixed inset-0 bg-black/30" onClick={() => setRejecting(null)} />
          <div className="relative w-full max-w-md rounded-lg bg-white p-4 shadow-xl">
            <h3 className="mb-2 text-lg font-semibold text-gray-900">Foglalás elutasítása</h3>
            <p className="mb-3 text-sm text-gray-500">
              Az indoklást a foglaló is látni fogja - ettől lesz érthető, miért nem jó.
            </p>
            <textarea
              rows={3}
              maxLength={500}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="pl. Aznap karbantartás van a pályán."
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            <div className="mt-3 flex justify-end gap-2">
              <button
                onClick={() => setRejecting(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Mégse
              </button>
              <button
                onClick={handleReject}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700"
              >
                Elutasítás
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Bookings;
